import React, { useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Mail,
  RefreshCw,
  Search,
  Users,
  X,
} from "lucide-react";
import {
  warehouseMonitoringService,
  WarehouseCrmProfile,
  WarehouseCrmProfileReservation,
  WarehouseCrmProfilesResult,
} from "../../../services/WarehouseMonitoringService";
import { WarehouseReservationDetailsContent } from "./WarehouseReservationDetailsContent";
import { WarehouseMultiSelectFilter } from "./WarehouseMultiSelectFilter";
import { useWarehouseFilterOptions } from "../../../hooks/useWarehouseFilterOptions";

function formatCurrency(value: number, currencyCode: string | null): string {
  const currency = currencyCode || "EUR";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value.toLocaleString("en-US")} ${currency}`;
  }
}

function formatDate(value: string | null): string {
  if (!value) {
    return "—";
  }

  return new Date(value).toLocaleDateString("en-US");
}

function reservationKey(reservation: WarehouseCrmProfileReservation): string {
  return `${reservation.hotelCode}-${reservation.pms}-${reservation.reservationId}`;
}

interface WarehouseCrmProps {
  initialEmail?: string | null;
  onInitialEmailConsumed?: () => void;
}

export function WarehouseCrm({ initialEmail, onInitialEmailConsumed }: WarehouseCrmProps) {
  const {
    hotelOptions,
    selectedHotelCodes,
    setSelectedHotelCodes,
    hotelCodeFilter,
    getHotelDisplayName,
    formatHotelCodeList,
    ensureHotelNames,
    statsError,
  } = useWarehouseFilterOptions();
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<WarehouseCrmProfilesResult | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<WarehouseCrmProfile | null>(null);
  const [reservations, setReservations] = useState<WarehouseCrmProfileReservation[]>([]);
  const [reservationsLoading, setReservationsLoading] = useState(false);
  const [expandedReservationKey, setExpandedReservationKey] = useState<string | null>(null);
  const [payloadSectionOpen, setPayloadSectionOpen] = useState(false);
  const [expandedPayload, setExpandedPayload] = useState<unknown | null>(null);
  const [payloadLoading, setPayloadLoading] = useState(false);

  const [emailFilter, setEmailFilter] = useState("");
  const [page, setPage] = useState(1);

  const loadProfiles = useCallback(
    async (pageNumber = page, showInitialLoader = false) => {
      if (showInitialLoader) {
        setLoading(true);
      } else {
        setTableLoading(true);
      }
      setError(null);

      try {
        const response = await warehouseMonitoringService.getCrmProfiles({
          email: emailFilter.trim() || undefined,
          hotelCode: hotelCodeFilter,
          page: pageNumber,
          pageSize: 25,
        });
        setResult(response);
        setPage(pageNumber);
        await ensureHotelNames(response.data.flatMap(profile => profile.hotelCodes));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load CRM data");
      } finally {
        setLoading(false);
        setTableLoading(false);
      }
    },
    [emailFilter, ensureHotelNames, hotelCodeFilter, page]
  );

  const loadReservations = useCallback(
    async (email: string) => {
      setReservationsLoading(true);
      setError(null);

      try {
        const response = await warehouseMonitoringService.getCrmProfileReservations(email, {
          hotelCode: hotelCodeFilter,
          page: 1,
          pageSize: 50,
        });
        setReservations(response.data);
        await ensureHotelNames(response.data.map(reservation => reservation.hotelCode));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load reservations");
        setReservations([]);
      } finally {
        setReservationsLoading(false);
      }
    },
    [ensureHotelNames, hotelCodeFilter]
  );

  useEffect(() => {
    loadProfiles(1, true);
  }, []);

  useEffect(() => {
    if (!initialEmail) {
      return;
    }

    const openProfileByEmail = async () => {
      setEmailFilter(initialEmail);
      setError(null);

      try {
        const response = await warehouseMonitoringService.getCrmProfiles({
          email: initialEmail,
          hotelCode: hotelCodeFilter,
          page: 1,
          pageSize: 25,
        });
        setResult(response);
        setPage(1);
        await ensureHotelNames(response.data.flatMap(profile => profile.hotelCodes));

        const profile =
          response.data.find((item) => item.email === initialEmail) ?? response.data[0] ?? null;

        if (profile) {
          setSelectedProfile(profile);
          setExpandedReservationKey(null);
          setPayloadSectionOpen(false);
          setExpandedPayload(null);
          await loadReservations(profile.email);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load CRM data");
      } finally {
        onInitialEmailConsumed?.();
      }
    };

    void openProfileByEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEmail]);

  useEffect(() => {
    if (!result?.data.length) {
      return;
    }

    void ensureHotelNames(result.data.flatMap(profile => profile.hotelCodes));
  }, [ensureHotelNames, result]);

  const handleSearch = () => {
    setSelectedProfile(null);
    setReservations([]);
    setExpandedReservationKey(null);
    setPayloadSectionOpen(false);
    setExpandedPayload(null);
    loadProfiles(1);
  };

  const handleSelectProfile = (profile: WarehouseCrmProfile) => {
    setSelectedProfile(profile);
    setExpandedReservationKey(null);
    setPayloadSectionOpen(false);
    setExpandedPayload(null);
    loadReservations(profile.email);
  };

  const handleClosePanel = () => {
    setSelectedProfile(null);
    setReservations([]);
    setExpandedReservationKey(null);
    setPayloadSectionOpen(false);
    setExpandedPayload(null);
  };

  const loadReservationPayload = useCallback(
    async (reservation: WarehouseCrmProfileReservation) => {
      const key = reservationKey(reservation);
      setPayloadLoading(true);

      try {
        const payload = await warehouseMonitoringService.getPayload(
          reservation.hotelCode,
          reservation.reservationId,
          reservation.pms,
          "latest",
        );
        setExpandedReservationKey((currentKey) => {
          if (currentKey === key) {
            setExpandedPayload(payload);
          }
          return currentKey;
        });
      } catch {
        setExpandedReservationKey((currentKey) => {
          if (currentKey === key) {
            setExpandedPayload({ error: "Unable to load reservation details" });
          }
          return currentKey;
        });
      } finally {
        setPayloadLoading(false);
      }
    },
    [],
  );

  const toggleReservation = (reservation: WarehouseCrmProfileReservation) => {
    const key = reservationKey(reservation);

    if (expandedReservationKey === key) {
      setExpandedReservationKey(null);
      setPayloadSectionOpen(false);
      setExpandedPayload(null);
      return;
    }

    setExpandedReservationKey(key);
    setPayloadSectionOpen(false);
    setExpandedPayload(null);
    void loadReservationPayload(reservation);
  };

  const togglePayloadSection = () => {
    setPayloadSectionOpen((prev) => !prev);
  };

  const totalPages = result ? Math.max(1, Math.ceil(result.meta.total / result.meta.pageSize)) : 1;

  if (loading) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">Loading CRM…</div>
      </div>
    );
  }

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="h-6 w-6 shrink-0 text-indigo-600" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">CRM profiles</h1>
              <p className="text-sm text-gray-500">
                Reservations consolidated by email address (warehouse)
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => loadProfiles(page)}
            className="flex shrink-0 items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      <div className="extranet-container-body">
        {(error || statsError) && (
          <div className="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error || statsError}</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 border border-gray-200 rounded-md">
            <div className="text-sm text-gray-500">Profiles</div>
            <div className="text-2xl font-semibold text-gray-900">
              {result?.summary.totalProfiles.toLocaleString("en-US") ?? 0}
            </div>
          </div>
          <div className="bg-white p-4 border border-gray-200 rounded-md">
            <div className="text-sm text-gray-500">Reservations with email</div>
            <div className="text-2xl font-semibold text-gray-900">
              {result?.summary.totalReservationsWithEmail.toLocaleString("en-US") ?? 0}
            </div>
          </div>
          <div className="bg-white p-4 border border-gray-200 rounded-md">
            <div className="text-sm text-gray-500">Without email</div>
            <div className="text-2xl font-semibold text-gray-900">
              {result?.summary.reservationsWithoutEmail.toLocaleString("en-US") ?? 0}
            </div>
          </div>
          <div className="bg-white p-4 border border-gray-200 rounded-md">
            <div className="text-sm text-gray-500">Rows analyzed</div>
            <div className="text-2xl font-semibold text-gray-900">
              {result?.summary.rowsAnalyzed.toLocaleString("en-US") ?? 0}
              {result?.summary.truncated ? " +" : ""}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-6 items-end">
          <input
            type="text"
            value={emailFilter}
            onChange={(event) => setEmailFilter(event.target.value)}
            placeholder="Search by email"
            className="form-input min-w-[220px]"
          />
          <WarehouseMultiSelectFilter
            label="Hotel"
            placeholder="All hotels"
            options={hotelOptions}
            selectedValues={selectedHotelCodes}
            onChange={setSelectedHotelCodes}
            className="min-w-[220px] flex-1"
          />
          <button
            type="button"
            onClick={handleSearch}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            <Search className="h-4 w-4" />
            Search
          </button>
        </div>

        <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900">Customer profiles</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Email</th>
                  <th className="table-header">Name</th>
                  <th className="table-header">Res.</th>
                  <th className="table-header">Revenue</th>
                  <th className="table-header">Hotels</th>
                  <th className="table-header">Last stay</th>
                  <th className="table-header"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tableLoading ? (
                  <tr>
                    <td colSpan={7} className="table-cell text-center text-gray-500 py-8">
                      Loading…
                    </td>
                  </tr>
                ) : result?.data.length ? (
                  result.data.map((profile) => (
                    <tr
                      key={profile.email}
                      className={`table-row cursor-pointer ${
                        selectedProfile?.email === profile.email ? "bg-indigo-50" : ""
                      }`}
                      onClick={() => handleSelectProfile(profile)}
                    >
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-gray-900">{profile.email}</span>
                        </div>
                      </td>
                      <td className="table-cell">{profile.displayName || "—"}</td>
                      <td className="table-cell">{profile.reservationCount}</td>
                      <td className="table-cell">
                        {formatCurrency(profile.totalRevenue, profile.currencyCode)}
                      </td>
                      <td className="table-cell">
                        {formatHotelCodeList(profile.hotelCodes) || "—"}
                      </td>
                      <td className="table-cell">{formatDate(profile.lastStayAt)}</td>
                      <td className="table-cell">
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="table-cell text-center text-gray-500 py-8">
                      No profiles found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <span className="text-sm text-gray-500">
              Page {page} / {totalPages} ({result?.meta.total ?? 0} profiles)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1 || tableLoading}
                onClick={() => loadProfiles(page - 1)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages || tableLoading}
                onClick={() => loadProfiles(page + 1)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {selectedProfile && (
          <div className="fixed inset-0 z-50 flex">
            <button
              type="button"
              aria-label="Close panel"
              className="absolute inset-0 bg-gray-900/40"
              onClick={handleClosePanel}
            />
            <aside className="ml-auto w-full max-w-xl bg-white shadow-xl overflow-y-auto h-full relative z-10 flex flex-col">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 truncate">
                    {selectedProfile.displayName || selectedProfile.email}
                  </h2>
                  <p className="text-sm text-gray-500 truncate">{selectedProfile.email}</p>
                </div>
                <button
                  type="button"
                  onClick={handleClosePanel}
                  className="p-1 text-gray-500 hover:text-gray-700 shrink-0"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-4 border-b border-gray-200 bg-gray-50 grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-gray-500">Reservations</div>
                  <div className="text-sm font-semibold text-gray-900">
                    {selectedProfile.reservationCount}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Total revenue</div>
                  <div className="text-sm font-semibold text-gray-900">
                    {formatCurrency(selectedProfile.totalRevenue, selectedProfile.currencyCode)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Nights</div>
                  <div className="text-sm font-semibold text-gray-900">
                    {selectedProfile.totalNights}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Hotels</div>
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    {formatHotelCodeList(selectedProfile.hotelCodes) || "—"}
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                <div className="px-4 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900">Reservations</h3>
                </div>
                {reservationsLoading ? (
                  <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
                ) : reservations.length ? (
                  <div className="divide-y divide-gray-200">
                    {reservations.map((reservation) => {
                      const key = reservationKey(reservation);
                      const isExpanded = expandedReservationKey === key;

                      return (
                        <div key={key} className="bg-white">
                          <button
                            type="button"
                            onClick={() => toggleReservation(reservation)}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-start justify-between gap-3"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                {isExpanded ? (
                                  <ChevronUp className="h-4 w-4 text-gray-400 shrink-0" />
                                ) : (
                                  <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                                )}
                                <span className="text-sm font-medium text-gray-900 truncate">
                                  {reservation.reservationId}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5 ml-6">
                                <span title={reservation.hotelCode}>
                                  {getHotelDisplayName(reservation.hotelCode)}
                                </span>{" "}
                                · {reservation.pms}
                                {!isExpanded && (
                                  <>
                                    {" · "}
                                    {formatDate(reservation.startAt)} → {formatDate(reservation.endAt)}
                                    {" · "}
                                    {formatCurrency(
                                      reservation.revenue,
                                      selectedProfile.currencyCode,
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 shrink-0">
                              {reservation.status || "—"}
                            </span>
                          </button>

                          {isExpanded && (
                            <div className="px-4 pb-4 ml-6 border-t border-gray-100 bg-gray-50/50">
                              <div className="pt-3 grid grid-cols-2 gap-3 text-xs text-gray-600 mb-3">
                                <div>
                                  <span className="text-gray-400">Arrival</span>
                                  <div className="font-medium text-gray-800">
                                    {formatDate(reservation.startAt)}
                                  </div>
                                </div>
                                <div>
                                  <span className="text-gray-400">Departure</span>
                                  <div className="font-medium text-gray-800">
                                    {formatDate(reservation.endAt)}
                                  </div>
                                </div>
                                <div>
                                  <span className="text-gray-400">Revenue</span>
                                  <div className="font-medium text-gray-800">
                                    {formatCurrency(
                                      reservation.revenue,
                                      selectedProfile.currencyCode,
                                    )}
                                  </div>
                                </div>
                                {reservation.channel && (
                                  <div>
                                    <span className="text-gray-400">Channel</span>
                                    <div className="font-medium text-gray-800">
                                      {reservation.channel}
                                    </div>
                                  </div>
                                )}
                              </div>

                              <WarehouseReservationDetailsContent
                                payload={expandedPayload}
                                loading={payloadLoading && expandedPayload === null}
                                locale="en"
                                payloadSectionOpen={payloadSectionOpen}
                                onTogglePayloadSection={togglePayloadSection}
                                compact
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-8 text-center text-sm text-gray-500">
                    No reservations for this profile
                  </div>
                )}
              </div>
            </aside>
          </div>
        )}

        <p className="mt-4 text-xs text-gray-500">
          Profiles are built from <code className="rounded bg-gray-100 px-1">payload.guest.email</code> in the
          warehouse. Reservations ingested via Lighthouse without an email (e.g. MisterBooking) do not appear
          here.
        </p>
      </div>
    </div>
  );
}
